/**
 * API client for ZTNA Customers endpoints (/api/v1/ztna/customers).
 *
 * v2 (Spec §10.4): global customers + per-app grants. Sem tenant_id no
 * customer; sem environments[]. Cada grant aponta para uma application_id
 * (que carrega tenant_id + environment).
 */
import { api } from '@/api/client'

// ─── Types ────────────────────────────────────────────────────────────────────

export type CustomerStatus = 'pending_password' | 'active' | 'revoked'

export interface GrantableApp {
  id: number
  slug: string
  display_name: string
  hostname: string
  tenant_id: number
  tenant_name: string
  environment: string
}

export interface CustomerAppGrant {
  id: number
  application_id: number
  application: GrantableApp
  expires_at: string
  granted_at: string
  granted_by_email?: string
  revoked_at?: string | null
  revocation_reason?: string | null
}

export interface ZtnaCustomer {
  id: number
  email: string
  full_name: string
  company: string | null
  phone: string | null
  status: CustomerStatus
  email_bounced: boolean
  created_at: string
  created_by_email?: string
  grants: CustomerAppGrant[]
  last_session_at?: string | null
  // Lock state + auth metadata (visibility — Task 117)
  password_set_at?: string | null
  last_login_at?: string | null
  failed_login_count?: number
  locked_until?: string | null
  is_locked?: boolean
}

/** Bloqueio de login ativo do cliente (GET /customers/{id}/bloqueios). */
export type CustomerBloqueioTipo = 'senha_errada' | 'cota_links' | 'tentativas_ip' | 'cota_reset'

export interface CustomerBloqueio {
  tipo: CustomerBloqueioTipo
  motivo: string
  /** ISO-8601 UTC. */
  libera_em: string
  /** Desbloquear limpa este bloqueio (a cota de redefinição de senha não). */
  desbloqueavel: boolean
  detalhe: Record<string, unknown>
}

export interface CreateCustomerBody {
  email: string
  full_name: string
  company?: string | null
  phone?: string | null
  application_ids: number[]
  expires_at: string
}

export interface UpdateCustomerBody {
  full_name?: string
  company?: string | null
  phone?: string | null
  notes?: string | null
}

export interface AddGrantsBody {
  application_ids: number[]
  expires_at: string
}

export interface ListCustomersResponse {
  items: ZtnaCustomer[]
  total: number
  limit: number
  offset: number
}

export interface ListCustomersParams {
  tenantId?: number
  status?: CustomerStatus
  search?: string
  limit?: number
  offset?: number
}

export interface CustomerSession {
  jti: string
  created_at: string
  expires_at: string
  ip?: string | null
  user_agent?: string | null
  revoked: boolean
}

export interface CustomerAuditEvent {
  id: number
  event_type: string
  action: string
  actor_email?: string | null
  created_at: string
  payload?: Record<string, unknown> | null
}

export interface CustomerAuditResponse {
  items: CustomerAuditEvent[]
  total: number
}

// ─── API functions ─────────────────────────────────────────────────────────────

const PREFIX = '/v1/ztna/customers'

export const ztnaCustomersApi = {
  listCustomers: async (
    opts: ListCustomersParams = {},
  ): Promise<ListCustomersResponse> => {
    const params: Record<string, string | number> = {}
    if (opts.tenantId !== undefined) params.tenant_id = opts.tenantId
    if (opts.status) params.status = opts.status
    if (opts.search) params.search = opts.search
    if (opts.limit !== undefined) params.limit = opts.limit
    if (opts.offset !== undefined) params.offset = opts.offset
    const r = await api.get<ListCustomersResponse>(PREFIX, { params })
    return r.data
  },

  getCustomer: async (id: number): Promise<ZtnaCustomer> => {
    const r = await api.get<ZtnaCustomer>(`${PREFIX}/${id}`)
    return r.data
  },

  createCustomer: async (body: CreateCustomerBody): Promise<ZtnaCustomer> => {
    const r = await api.post<ZtnaCustomer>(PREFIX, body)
    return r.data
  },

  updateCustomer: async (
    id: number,
    body: UpdateCustomerBody,
  ): Promise<ZtnaCustomer> => {
    const r = await api.patch<ZtnaCustomer>(`${PREFIX}/${id}`, body)
    return r.data
  },

  revokeCustomer: async (
    id: number,
    reason?: string,
  ): Promise<ZtnaCustomer> => {
    const r = await api.post<ZtnaCustomer>(`${PREFIX}/${id}/revoke`, { reason })
    return r.data
  },

  deleteCustomer: async (id: number): Promise<void> => {
    await api.delete(`${PREFIX}/${id}`)
  },

  reactivateCustomer: async (id: number): Promise<ZtnaCustomer> => {
    const r = await api.post<ZtnaCustomer>(`${PREFIX}/${id}/reactivate`)
    return r.data
  },

  unlockCustomer: async (id: number): Promise<ZtnaCustomer> => {
    const r = await api.post<ZtnaCustomer>(`${PREFIX}/${id}/unlock`)
    return r.data
  },

  reactivateGrant: async (id: number, grantId: number): Promise<CustomerAppGrant> => {
    const r = await api.post<CustomerAppGrant>(`${PREFIX}/${id}/grants/${grantId}/reactivate`)
    return r.data
  },

  deleteGrant: async (id: number, grantId: number): Promise<void> => {
    await api.delete(`${PREFIX}/${id}/grants/${grantId}`)
  },

  addGrants: async (
    id: number,
    body: AddGrantsBody,
  ): Promise<{ grants_added: CustomerAppGrant[] }> => {
    const r = await api.post<{ grants_added: CustomerAppGrant[] }>(
      `${PREFIX}/${id}/grants`,
      body,
    )
    return r.data
  },

  renewGrant: async (
    id: number,
    grantId: number,
    expiresAt: string,
  ): Promise<CustomerAppGrant> => {
    const r = await api.patch<CustomerAppGrant>(
      `${PREFIX}/${id}/grants/${grantId}`,
      { expires_at: expiresAt },
    )
    return r.data
  },

  revokeGrant: async (
    id: number,
    grantId: number,
    reason?: string,
  ): Promise<void> => {
    await api.post(`${PREFIX}/${id}/grants/${grantId}/revoke`, { reason })
  },

  listGrantableApps: async (search?: string): Promise<GrantableApp[]> => {
    const params: Record<string, string> = {}
    if (search) params.search = search
    const r = await api.get<GrantableApp[]>('/v1/ztna/customer-grantable-apps', {
      params,
    })
    return r.data
  },

  resendInvitation: async (id: number): Promise<void> => {
    await api.post(`${PREFIX}/${id}/resend-invitation`)
  },

  getCustomerBloqueios: async (id: number): Promise<CustomerBloqueio[]> => {
    const r = await api.get<CustomerBloqueio[]>(`${PREFIX}/${id}/bloqueios`)
    return r.data
  },

  listSessions: async (id: number): Promise<CustomerSession[]> => {
    const r = await api.get<CustomerSession[]>(`${PREFIX}/${id}/sessions`)
    return r.data
  },

  revokeSession: async (id: number, jti: string): Promise<void> => {
    await api.post(`${PREFIX}/${id}/sessions/${jti}/revoke`)
  },

  getAudit: async (id: number): Promise<CustomerAuditResponse> => {
    const r = await api.get<CustomerAuditResponse>(`${PREFIX}/${id}/audit`)
    return r.data
  },
}

export type ZtnaCustomersApi = typeof ztnaCustomersApi
